import { lazy, Suspense } from "react";
import { Loader2, Sparkles, Hammer } from "lucide-react";
import { Button } from "../ui/button";
import { solutionStatus } from "../blocks/solutionTools";

const BlocksSolutionView = lazy(() => import("../blocks/BlocksSolutionView").then((m) => ({ default: m.BlocksSolutionView })));

// Admin question dialog: status of the block solution + the actions that create one (Junior and Blocks tracks)
export function BlockSolutionPanel({ question, busy, generating, converting, onGenerateNav, onConvert, onBuild, showPreview, onTogglePreview }) {
  const status = solutionStatus(question);
  const isJunior = question.mode === "junior";
  const isNew = !question.question_id;
  const hasPython = !!(question.sample_solution || "").trim();
  const verified = status.state === "verified";

  return (
    <div className="rounded-lg bg-muted/40 p-3 space-y-2" data-testid="question-blocks-solution-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm">
          <span className="font-medium">Block solution: </span>
          {verified ? (
            <span className="text-emerald-600" data-testid="question-blocks-solution-status">Verified ({status.source === "auto" ? (isJunior ? "auto-generated" : "auto-converted") : "built on the site"})</span>
          ) : (
            <span className="text-amber-600" data-testid="question-blocks-solution-status">None yet{status.note ? ` - ${status.note}` : ""}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {isJunior ? (
            <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5" disabled={generating || busy} onClick={onGenerateNav} data-testid="question-nav-solution-generate-btn">
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Generate block solution
            </Button>
          ) : (
            <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5" disabled={converting || busy || !hasPython} title={hasPython ? "Convert the Python sample into blocks and verify them" : "Add a Python sample first to auto-convert"} onClick={onConvert} data-testid="question-blocks-solution-convert-btn">
              {converting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Auto-convert from Python
            </Button>
          )}
          <Button type="button" variant={isJunior ? "outline" : "default"} size="sm" className={`h-8 gap-1.5 ${isJunior ? "border-amber-400 text-amber-700 dark:text-amber-300" : "bg-sky-600 hover:bg-sky-700 text-white"}`} disabled={busy || generating} onClick={onBuild} data-testid="question-blocks-solution-build-btn">
            <Hammer className="w-3.5 h-3.5" /> {verified ? "Edit in blocks" : "Build in blocks"}
          </Button>
          {verified && (
            <Button type="button" variant="ghost" size="sm" className="h-8" onClick={onTogglePreview} data-testid="question-blocks-solution-preview-btn">
              {showPreview ? "Hide preview" : "Preview"}
            </Button>
          )}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        {isJunior
          ? `"Generate block solution" ${isNew ? "creates" : "saves"} the question, then auto-solves the grid (fewest turns, loops added to fit the block limit). "Build in blocks" also saves first, then opens a full-size block editor with the grid so you can build and play a program yourself.`
          : `"Build in blocks" ${isNew ? "creates" : "saves"} the question first, then opens a full-size block editor that tests your blocks against the test cases below - add at least one test case first. The blocks' Python fills the optional Python sample if it is empty. You can also solve it in Blocks mode on the site and click "Save as sample solution".`}
      </p>
      {showPreview && verified && (
        <Suspense fallback={<div className="text-xs text-muted-foreground">Loading blocks…</div>}>
          <BlocksSolutionView workspace={question.blocks.solution_workspace} inputs={question.blocks.inputs || []} python={question.blocks.solution_python || ""} maxHeight={360} testId="question-blocks-solution-preview" />
        </Suspense>
      )}
    </div>
  );
}
